import { COLUMN_CATALOG_MAP } from './constants'

function resolveSourceLabel (col, allColumns) {
  const sourceCol = col.options?.sourceColumnId
    ? allColumns.find(c => c.id === col.options.sourceColumnId)
    : null
  if (sourceCol) {
    return sourceCol.label ?? COLUMN_CATALOG_MAP[sourceCol.columnType]?.label ?? sourceCol.columnType
  }
  return COLUMN_CATALOG_MAP[col.options?.sourceColumnType]?.label || col.options?.sourceColumnType || 'MV'
}

function buildWindowHeader (col, catalog, allColumns) {
  const sourceLabel = resolveSourceLabel(col, allColumns)
  const strategyLabel = col.options?.strategy === 'toRoot' ? 'Total' : 'Parent'

  switch (catalog.windowType) {
    case 'variance':
      return `Variance to ${strategyLabel} ${sourceLabel}`
    case 'rank': {
      const dir = col.options?.sortDirection === 'asc' ? 'Asc' : 'Desc'
      return `Rank by ${sourceLabel} (${dir})`
    }
    case 'percentileRank': {
      const dir = col.options?.sortDirection === 'asc' ? 'Asc' : 'Desc'
      return `Pctl Rank ${sourceLabel} (${dir})`
    }
    case 'concentration':
      return `Concentration by ${sourceLabel}`
    default:
      return `% of ${strategyLabel} ${sourceLabel}`
  }
}

function resolveColLabel (id, type, allColumns) {
  const inst = id ? allColumns.find(c => c.id === id) : null
  if (inst) return inst.label ?? COLUMN_CATALOG_MAP[inst.columnType]?.label ?? inst.columnType
  return COLUMN_CATALOG_MAP[type]?.label || type || '?'
}

function buildComparisonHeader (col, allColumns) {
  const labelA = resolveColLabel(col.options?.columnAId, col.options?.columnAType, allColumns)
  const labelB = resolveColLabel(col.options?.columnBId, col.options?.columnBType, allColumns)
  const op = col.options?.operation || 'difference'

  switch (op) {
    case 'ratio':
      return `${labelA} / ${labelB}`
    case 'percentChange':
      return `% Chg ${labelA} vs ${labelB}`
    case 'absoluteDifference':
      return `|${labelA} - ${labelB}|`
    default:
      return `${labelA} - ${labelB}`
  }
}

/**
 * Resolves the display header for a column instance.
 *
 * Priority: user override (col.label) > kind-specific smart default > catalog headerFn > catalog.label.
 *
 * @param {object} col - Column instance from config.columns
 * @param {object} catalog - Catalog entry from COLUMN_CATALOG_MAP
 * @param {object[]} allColumns - Full config.columns array (needed for window/comparison source resolution)
 * @returns {string}
 */
export function resolveColumnHeader (col, catalog, allColumns) {
  if (col.label) return col.label
  if (catalog.kind === 'window') return buildWindowHeader(col, catalog, allColumns)
  if (catalog.kind === 'comparison') return buildComparisonHeader(col, allColumns)
  if (catalog.headerFn) return catalog.headerFn(col.options)
  return catalog.label
}

/**
 * Returns the smart default header (tier 2) ignoring any user override.
 * Used as placeholder text in the rename input.
 */
export function resolveDefaultHeader (col, catalog, allColumns) {
  if (catalog.kind === 'window') return buildWindowHeader(col, catalog, allColumns)
  if (catalog.kind === 'comparison') return buildComparisonHeader(col, allColumns)
  if (catalog.headerFn) return catalog.headerFn(col.options)
  return catalog.label
}
